import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";

interface Session {
  id: string;
  customerName: string | null;
  customerEmail: string | null;
  sessionType: "browse" | "takeback" | "voice_assist";
  status: "active" | "completed" | "abandoned";
  startedAt: string;
  endedAt: string | null;
  messageCount: number;
  productsViewed: number;
}

interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  productContext?: {
    name: string;
    price: number;
    imageUrl: string;
  };
}

interface TakebackItem {
  id: string;
  name: string;
  condition: "excellent" | "good" | "fair";
  originalPrice: number;
  creditValue: number;
  imageUrl?: string;
}

interface Takeback {
  id: string;
  sessionId: string;
  customerName: string;
  customerEmail?: string;
  itemCount: number;
  totalCredit: number;
  status: "pending" | "completed" | "declined";
  createdAt: string;
  items: TakebackItem[];
  // Conversion tracking
  converted: boolean;
  conversionOrder?: {
    orderId: string;
    orderTotal: number;
    creditUsed: number;
    newPayment: number;
    products: { name: string; price: number }[];
    orderDate: string;
  };
}

export const Route = createFileRoute("/pin_/$id")({
  component: PINDetailPage,
});

function PINDetailPage() {
  const { id } = Route.useParams() as { id: string };
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState<
    "sessions" | "chats" | "takebacks"
  >("sessions");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [takebacks, setTakebacks] = useState<Takeback[]>([]);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedTakeback, setSelectedTakeback] = useState<Takeback | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [pinInfo, setPinInfo] = useState<{
    name: string;
    location: string;
    status: string;
  } | null>(null);

  useEffect(() => {
    loadPINData();
  }, [id, currentTenant.id]);

  const loadPINData = async () => {
    try {
      // Fetch sessions for this PIN
      const sessionsRes = await fetch(`/api/v1/pin/sessions?kioskId=${id}`);
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        if (data.sessions && data.sessions.length > 0) {
          setSessions(data.sessions);
        } else {
          setDemoData();
        }
      } else {
        setDemoData();
      }
    } catch (error) {
      console.error("Failed to load PIN data:", error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    if (currentTenant.id === "arcteryx") {
      setArcteryxDemoData();
    } else if (currentTenant.id === "adidas") {
      setAdidasDemoData();
    } else if (currentTenant.id === "nike") {
      setNikeDemoData();
    } else {
      setLululemonDemoData();
    }
  };

  const setArcteryxDemoData = () => {
    // Arc'teryx sessions
    setSessions([
      {
        id: "ses-001",
        customerName: "Customer #10482",
        customerEmail: null,
        sessionType: "voice_assist",
        status: "completed",
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1200000).toISOString(),
        messageCount: 8,
        productsViewed: 5,
      },
      {
        id: "ses-002",
        customerName: null,
        customerEmail: null,
        sessionType: "browse",
        status: "completed",
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3000000).toISOString(),
        messageCount: 0,
        productsViewed: 12,
      },
      {
        id: "ses-003",
        customerName: "Customer #10495",
        customerEmail: null,
        sessionType: "takeback",
        status: "completed",
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 6600000).toISOString(),
        messageCount: 6,
        productsViewed: 3,
      },
    ]);

    // Arc'teryx messages
    setMessages([
      {
        id: "msg-001",
        sessionId: "ses-001",
        role: "user",
        content: "I need a jacket for backcountry skiing",
        timestamp: new Date(Date.now() - 1700000).toISOString(),
      },
      {
        id: "msg-002",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "For backcountry skiing, I'd recommend the Beta AR or Alpha SV. Both feature GORE-TEX Pro for maximum weather protection.",
        timestamp: new Date(Date.now() - 1690000).toISOString(),
      },
      {
        id: "msg-003",
        sessionId: "ses-001",
        role: "user",
        content: "What's the difference between them?",
        timestamp: new Date(Date.now() - 1680000).toISOString(),
      },
      {
        id: "msg-004",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "The Alpha SV is our most durable shell, built for severe alpine conditions. The Beta AR is more versatile for all-round mountain use with a slightly more relaxed fit.",
        timestamp: new Date(Date.now() - 1670000).toISOString(),
        productContext: {
          name: "Beta AR Jacket",
          price: 625,
          imageUrl: "/images/products/arcteryx/beta-ar-jacket.jpeg",
        },
      },
      {
        id: "msg-005",
        sessionId: "ses-003",
        role: "user",
        content: "I have some old Arc'teryx gear to trade in",
        timestamp: new Date(Date.now() - 7100000).toISOString(),
      },
      {
        id: "msg-006",
        sessionId: "ses-003",
        role: "assistant",
        content:
          "Excellent! Arc'teryx gear holds its value well. Please place your items on the assessment area so I can evaluate their condition.",
        timestamp: new Date(Date.now() - 7090000).toISOString(),
      },
    ]);

    // Arc'teryx takebacks
    setTakebacks([
      {
        id: "tb-001",
        sessionId: "ses-003",
        customerName: "Customer #10495",
        itemCount: 2,
        totalCredit: 280,
        status: "completed",
        createdAt: new Date(Date.now() - 6800000).toISOString(),
        items: [
          {
            id: "item-1",
            name: "Beta AR Jacket",
            condition: "excellent",
            originalPrice: 625,
            creditValue: 200,
            imageUrl: "/images/products/arcteryx/beta-ar-jacket.jpeg",
          },
          {
            id: "item-2",
            name: "Atom LT Hoody",
            condition: "good",
            originalPrice: 300,
            creditValue: 80,
            imageUrl: "/images/products/arcteryx/atom-lt-hoody.jpeg",
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ARC-2024-1142",
          orderTotal: 1100,
          creditUsed: 280,
          newPayment: 820,
          products: [{ name: "Alpha SV Jacket", price: 1100 }],
          orderDate: new Date(Date.now() - 6700000).toISOString(),
        },
      },
      {
        id: "tb-002",
        sessionId: "ses-005",
        customerName: "Customer #10503",
        itemCount: 3,
        totalCredit: 315,
        status: "completed",
        createdAt: new Date(Date.now() - 14400000).toISOString(),
        items: [
          {
            id: "item-3",
            name: "Cerium LT Jacket",
            condition: "excellent",
            originalPrice: 450,
            creditValue: 155,
            imageUrl: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
          },
          {
            id: "item-4",
            name: "Gamma LT Pants",
            condition: "good",
            originalPrice: 250,
            creditValue: 85,
            imageUrl: "/images/products/arcteryx/gamma-lt-pant.jpeg",
          },
          {
            id: "item-5",
            name: "Delta LT Jacket",
            condition: "good",
            originalPrice: 200,
            creditValue: 75,
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ARC-2024-1098",
          orderTotal: 625,
          creditUsed: 315,
          newPayment: 310,
          products: [{ name: "Beta AR Jacket", price: 625 }],
          orderDate: new Date(Date.now() - 14300000).toISOString(),
        },
      },
      {
        id: "tb-003",
        sessionId: "ses-007",
        customerName: "Guest",
        itemCount: 1,
        totalCredit: 95,
        status: "declined",
        createdAt: new Date(Date.now() - 28800000).toISOString(),
        items: [
          {
            id: "item-6",
            name: "Proton FL Hoody",
            condition: "fair",
            originalPrice: 300,
            creditValue: 95,
            imageUrl: "/images/products/arcteryx/proton-fl-hoody.jpeg",
          },
        ],
        converted: false,
      },
    ]);

    // Arc'teryx PIN info
    if (id === "pin-van-001") {
      setPinInfo({
        name: "Vancouver Flagship",
        location: "Vancouver, BC",
        status: "online",
      });
    } else if (id === "pin-whi-001") {
      setPinInfo({
        name: "Whistler Village",
        location: "Whistler, BC",
        status: "online",
      });
    } else if (id === "pin-sea-001") {
      setPinInfo({
        name: "Seattle Premium",
        location: "Seattle, WA",
        status: "online",
      });
    } else {
      setPinInfo({
        name: "Arc'teryx PIN Device",
        location: id,
        status: "online",
      });
    }
  };

  const setLululemonDemoData = () => {
    // Lululemon sessions
    setSessions([
      {
        id: "ses-001",
        customerName: "Customer #70229",
        customerEmail: null,
        sessionType: "voice_assist",
        status: "completed",
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1200000).toISOString(),
        messageCount: 12,
        productsViewed: 4,
      },
      {
        id: "ses-002",
        customerName: null,
        customerEmail: null,
        sessionType: "browse",
        status: "completed",
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3000000).toISOString(),
        messageCount: 0,
        productsViewed: 8,
      },
      {
        id: "ses-003",
        customerName: "Customer #70201",
        customerEmail: null,
        sessionType: "takeback",
        status: "completed",
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 6600000).toISOString(),
        messageCount: 5,
        productsViewed: 2,
      },
    ]);

    // Lululemon messages
    setMessages([
      {
        id: "msg-001",
        sessionId: "ses-001",
        role: "user",
        content: "Hi, I'm looking for leggings for yoga",
        timestamp: new Date(Date.now() - 1700000).toISOString(),
      },
      {
        id: "msg-002",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "The Align collection is perfect for yoga! They're made with our buttery-soft Nulu fabric.",
        timestamp: new Date(Date.now() - 1690000).toISOString(),
      },
      {
        id: "msg-003",
        sessionId: "ses-001",
        role: "user",
        content: "What sizes do you have?",
        timestamp: new Date(Date.now() - 1680000).toISOString(),
      },
      {
        id: "msg-004",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "We have sizes 2-14 in stock. Based on your history, you usually wear a size 6. Would you like me to check availability?",
        timestamp: new Date(Date.now() - 1670000).toISOString(),
        productContext: {
          name: 'Align High-Rise Pant 25"',
          price: 98,
          imageUrl: "/assets/images/lululemon/align-pant.jpg",
        },
      },
      {
        id: "msg-005",
        sessionId: "ses-003",
        role: "user",
        content: "I want to trade in some old leggings",
        timestamp: new Date(Date.now() - 7100000).toISOString(),
      },
      {
        id: "msg-006",
        sessionId: "ses-003",
        role: "assistant",
        content:
          "Great! I can help with that. Please place your items on the scale to start the assessment.",
        timestamp: new Date(Date.now() - 7090000).toISOString(),
      },
    ]);

    // Lululemon takebacks
    setTakebacks([
      {
        id: "tb-001",
        sessionId: "ses-003",
        customerName: "Customer #70201",
        itemCount: 2,
        totalCredit: 45,
        status: "completed",
        createdAt: new Date(Date.now() - 6800000).toISOString(),
        items: [
          {
            id: "item-1",
            name: 'Align High-Rise Pant 25"',
            condition: "excellent",
            originalPrice: 98,
            creditValue: 30,
            imageUrl: "/images/lululemon/align-pant.jpg",
          },
          {
            id: "item-2",
            name: "Energy Bra",
            condition: "good",
            originalPrice: 58,
            creditValue: 15,
            imageUrl: "/images/lululemon/offer-bra.jpg",
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ORD-2024-8821",
          orderTotal: 118,
          creditUsed: 45,
          newPayment: 73,
          products: [{ name: "Scuba Oversized Half-Zip", price: 118 }],
          orderDate: new Date(Date.now() - 6700000).toISOString(),
        },
      },
      {
        id: "tb-002",
        sessionId: "ses-005",
        customerName: "Customer #70218",
        itemCount: 3,
        totalCredit: 72,
        status: "completed",
        createdAt: new Date(Date.now() - 14400000).toISOString(),
        items: [
          {
            id: "item-3",
            name: 'Wunder Train High-Rise Tight 28"',
            condition: "excellent",
            originalPrice: 98,
            creditValue: 35,
            imageUrl: "/images/lululemon/wunder-train.jpg",
          },
          {
            id: "item-4",
            name: "Define Jacket",
            condition: "good",
            originalPrice: 118,
            creditValue: 25,
            imageUrl: "/images/lululemon/scuba-hoodie.jpg",
          },
          {
            id: "item-5",
            name: "Swiftly Tech Long Sleeve",
            condition: "fair",
            originalPrice: 78,
            creditValue: 12,
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ORD-2024-8798",
          orderTotal: 196,
          creditUsed: 72,
          newPayment: 124,
          products: [
            { name: 'Align High-Rise Pant 25"', price: 98 },
            { name: 'Align High-Rise Pant 25"', price: 98 },
          ],
          orderDate: new Date(Date.now() - 14300000).toISOString(),
        },
      },
      {
        id: "tb-003",
        sessionId: "ses-007",
        customerName: "Guest",
        itemCount: 1,
        totalCredit: 25,
        status: "declined",
        createdAt: new Date(Date.now() - 28800000).toISOString(),
        items: [
          {
            id: "item-6",
            name: "Fast and Free Tight",
            condition: "fair",
            originalPrice: 128,
            creditValue: 25,
          },
        ],
        converted: false,
      },
    ]);

    // Lululemon PIN info
    if (id === "pin-nj-001") {
      setPinInfo({
        name: "Short Hills Mall",
        location: "Short Hills, NJ",
        status: "online",
      });
    } else if (id === "pin-nyc-001") {
      setPinInfo({
        name: "Fifth Avenue Flagship",
        location: "New York, NY",
        status: "online",
      });
    } else {
      setPinInfo({
        name: "PIN Device",
        location: "Unknown",
        status: "offline",
      });
    }
  };

  const setAdidasDemoData = () => {
    // adidas Infinite Play sessions
    setSessions([
      {
        id: "ses-001",
        customerName: "Customer #40291",
        customerEmail: null,
        sessionType: "voice_assist",
        status: "completed",
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1200000).toISOString(),
        messageCount: 8,
        productsViewed: 5,
      },
      {
        id: "ses-002",
        customerName: null,
        customerEmail: null,
        sessionType: "browse",
        status: "completed",
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3000000).toISOString(),
        messageCount: 0,
        productsViewed: 11,
      },
      {
        id: "ses-003",
        customerName: "Customer #40318",
        customerEmail: null,
        sessionType: "takeback",
        status: "completed",
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 6600000).toISOString(),
        messageCount: 4,
        productsViewed: 3,
      },
    ]);

    // adidas messages
    setMessages([
      {
        id: "msg-001",
        sessionId: "ses-001",
        role: "user",
        content: "What is Boost technology and which shoes have it?",
        timestamp: new Date(Date.now() - 1700000).toISOString(),
      },
      {
        id: "msg-002",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "Boost is our signature cushioning technology made from TPU pellets. It provides exceptional energy return. You'll find it in the Ultraboost, NMD, and Pure Boost lines.",
        timestamp: new Date(Date.now() - 1690000).toISOString(),
      },
      {
        id: "msg-003",
        sessionId: "ses-001",
        role: "user",
        content: "Which one is best for daily running?",
        timestamp: new Date(Date.now() - 1680000).toISOString(),
      },
      {
        id: "msg-004",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "The Ultraboost is our top pick for daily running. It combines Boost midsole with a Primeknit upper for a snug, adaptive fit.",
        timestamp: new Date(Date.now() - 1670000).toISOString(),
        productContext: {
          name: "Ultraboost Light",
          price: 190,
          imageUrl:
            "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/fbaf991a78bc4896a3e9ad7800abcec6_9366/Ultraboost_22_Shoes_Black_GZ0127_01_standard.jpg",
        },
      },
      {
        id: "msg-005",
        sessionId: "ses-003",
        role: "user",
        content: "I have some shoes and a jacket to trade in",
        timestamp: new Date(Date.now() - 7100000).toISOString(),
      },
      {
        id: "msg-006",
        sessionId: "ses-003",
        role: "assistant",
        content:
          "Welcome to adidas Infinite Play! Place your items on the assessment area and I'll evaluate them for trade-in credit.",
        timestamp: new Date(Date.now() - 7090000).toISOString(),
      },
    ]);

    // adidas takebacks
    setTakebacks([
      {
        id: "tb-001",
        sessionId: "ses-003",
        customerName: "Customer #40291",
        itemCount: 2,
        totalCredit: 72,
        status: "completed",
        createdAt: new Date(Date.now() - 6800000).toISOString(),
        items: [
          {
            id: "item-1",
            name: "Ultraboost 22",
            condition: "excellent",
            originalPrice: 190,
            creditValue: 52,
            imageUrl:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/fbaf991a78bc4896a3e9ad7800abcec6_9366/Ultraboost_22_Shoes_Black_GZ0127_01_standard.jpg",
          },
          {
            id: "item-2",
            name: "Stan Smith",
            condition: "good",
            originalPrice: 100,
            creditValue: 20,
            imageUrl:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/68ae7ea7849b43eca70aac1e00f5146d_9366/Stan_Smith_Shoes_White_FX5502_01_standard.jpg",
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ADI-2026-0291",
          orderTotal: 180,
          creditUsed: 72,
          newPayment: 108,
          products: [{ name: "Ultraboost Light", price: 180 }],
          orderDate: new Date(Date.now() - 6700000).toISOString(),
        },
      },
      {
        id: "tb-002",
        sessionId: "ses-005",
        customerName: "Customer #40318",
        itemCount: 3,
        totalCredit: 93,
        status: "completed",
        createdAt: new Date(Date.now() - 14400000).toISOString(),
        items: [
          {
            id: "item-3",
            name: "Forum Low",
            condition: "excellent",
            originalPrice: 110,
            creditValue: 35,
            imageUrl:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/2e4ccfc0a2c14f20a4e5af050119f0ec_9366/Forum_Low_Shoes_White_FY7755_01_standard.jpg",
          },
          {
            id: "item-4",
            name: "Samba OG",
            condition: "good",
            originalPrice: 110,
            creditValue: 30,
            imageUrl:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/7ed0855435194229a525aad6009a0497_9366/Samba_OG_Shoes_White_B75806_01_standard.jpg",
          },
          {
            id: "item-5",
            name: "Gazelle",
            condition: "excellent",
            originalPrice: 100,
            creditValue: 28,
            imageUrl:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/9a06a2f7ac1c4c9c81afacd500f9b6a4_9366/Gazelle_Shoes_Black_BB5476_01_standard.jpg",
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "ADI-2026-0318",
          orderTotal: 220,
          creditUsed: 93,
          newPayment: 127,
          products: [
            { name: "Handball Spezial", price: 110 },
            { name: "Adicolor Trefoil Hoodie", price: 110 },
          ],
          orderDate: new Date(Date.now() - 14300000).toISOString(),
        },
      },
      {
        id: "tb-003",
        sessionId: "ses-007",
        customerName: "Guest",
        itemCount: 1,
        totalCredit: 28,
        status: "declined",
        createdAt: new Date(Date.now() - 28800000).toISOString(),
        items: [
          {
            id: "item-6",
            name: "NMD_R1",
            condition: "fair",
            originalPrice: 150,
            creditValue: 28,
          },
        ],
        converted: false,
      },
    ]);

    // adidas PIN info
    if (id === "pin-adidas-nyc-001") {
      setPinInfo({
        name: "adidas 5th Avenue NYC",
        location: "New York, NY",
        status: "online",
      });
    } else if (id === "pin-adidas-la-001") {
      setPinInfo({
        name: "adidas Originals LA",
        location: "Los Angeles, CA",
        status: "online",
      });
    } else if (id === "pin-adidas-chi-001") {
      setPinInfo({
        name: "adidas Chicago",
        location: "Chicago, IL",
        status: "online",
      });
    } else {
      setPinInfo({
        name: "adidas PIN Device",
        location: id,
        status: "online",
      });
    }
  };

  const setNikeDemoData = () => {
    // Nike sessions
    setSessions([
      {
        id: "ses-001",
        customerName: "Customer #50412",
        customerEmail: null,
        sessionType: "voice_assist",
        status: "completed",
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1200000).toISOString(),
        messageCount: 10,
        productsViewed: 6,
      },
      {
        id: "ses-002",
        customerName: null,
        customerEmail: null,
        sessionType: "browse",
        status: "completed",
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3000000).toISOString(),
        messageCount: 0,
        productsViewed: 15,
      },
      {
        id: "ses-003",
        customerName: "Customer #50428",
        customerEmail: null,
        sessionType: "takeback",
        status: "completed",
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 6600000).toISOString(),
        messageCount: 6,
        productsViewed: 4,
      },
    ]);

    // Nike messages
    setMessages([
      {
        id: "msg-001",
        sessionId: "ses-001",
        role: "user",
        content: "I'm looking for running shoes for marathon training",
        timestamp: new Date(Date.now() - 1700000).toISOString(),
      },
      {
        id: "msg-002",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "For marathon training, I'd recommend the Pegasus 41 or Vomero 18. Both feature Nike's ReactX foam for responsive cushioning over long distances.",
        timestamp: new Date(Date.now() - 1690000).toISOString(),
      },
      {
        id: "msg-003",
        sessionId: "ses-001",
        role: "user",
        content: "What's the difference between them?",
        timestamp: new Date(Date.now() - 1680000).toISOString(),
      },
      {
        id: "msg-004",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "The Pegasus 41 is our most versatile trainer, great for daily runs. The Vomero 18 has more cushioning and is better for longer recovery runs.",
        timestamp: new Date(Date.now() - 1670000).toISOString(),
        productContext: {
          name: "Pegasus 41",
          price: 140,
          imageUrl:
            "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/c0e93f87-b5c1-42c0-bda4-f6c8ccb3af3f/pegasus-41-road-running-shoes.png",
        },
      },
      {
        id: "msg-005",
        sessionId: "ses-001",
        role: "user",
        content: "Do you have any Jordan 1s in stock?",
        timestamp: new Date(Date.now() - 1660000).toISOString(),
      },
      {
        id: "msg-006",
        sessionId: "ses-001",
        role: "assistant",
        content:
          "Yes! We have the Air Jordan 1 Retro High OG in several colorways. Would you like to see what we have in your size?",
        timestamp: new Date(Date.now() - 1650000).toISOString(),
        productContext: {
          name: "Air Jordan 1 Retro High OG",
          price: 180,
          imageUrl:
            "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/4ca942e0-c08a-4eaf-a41b-1782ddea9c3b/air-jordan-1-retro-high-og-shoes.png",
        },
      },
      {
        id: "msg-007",
        sessionId: "ses-003",
        role: "user",
        content: "I want to trade in some old sneakers for credit",
        timestamp: new Date(Date.now() - 7100000).toISOString(),
      },
      {
        id: "msg-008",
        sessionId: "ses-003",
        role: "assistant",
        content:
          "Welcome to Nike Refurbished! Place your Nike or Jordan footwear on the scanner and I'll assess their condition for trade-in credit.",
        timestamp: new Date(Date.now() - 7090000).toISOString(),
      },
    ]);

    // Nike takebacks
    setTakebacks([
      {
        id: "tb-001",
        sessionId: "ses-003",
        customerName: "Customer #50428",
        itemCount: 3,
        totalCredit: 141,
        status: "completed",
        createdAt: new Date(Date.now() - 6800000).toISOString(),
        items: [
          {
            id: "item-1",
            name: "Pegasus 41",
            condition: "excellent",
            originalPrice: 140,
            creditValue: 63,
            imageUrl:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/c0e93f87-b5c1-42c0-bda4-f6c8ccb3af3f/pegasus-41-road-running-shoes.png",
          },
          {
            id: "item-2",
            name: "Dunk Low Retro",
            condition: "good",
            originalPrice: 110,
            creditValue: 33,
            imageUrl:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b1bcbca4-e853-4df7-b329-5be3c61ee057/dunk-low-retro-shoes.png",
          },
          {
            id: "item-3",
            name: "Air Max 90",
            condition: "excellent",
            originalPrice: 130,
            creditValue: 45,
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "NIKE-2024-5521",
          orderTotal: 280,
          creditUsed: 141,
          newPayment: 139,
          products: [
            { name: "Air Jordan 1 Retro High OG", price: 180 },
            { name: "Nike Socks 3-Pack", price: 100 },
          ],
          orderDate: new Date(Date.now() - 6700000).toISOString(),
        },
      },
      {
        id: "tb-002",
        sessionId: "ses-005",
        customerName: "Customer #50412",
        itemCount: 2,
        totalCredit: 103,
        status: "completed",
        createdAt: new Date(Date.now() - 14400000).toISOString(),
        items: [
          {
            id: "item-4",
            name: "Air Force 1 '07",
            condition: "excellent",
            originalPrice: 110,
            creditValue: 49,
            imageUrl:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b7d9211c-26e7-431a-ac24-b0540fb3c00f/air-force-1-07-shoes.png",
          },
          {
            id: "item-5",
            name: "Air Jordan 1 Retro High OG",
            condition: "good",
            originalPrice: 180,
            creditValue: 54,
            imageUrl:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/4ca942e0-c08a-4eaf-a41b-1782ddea9c3b/air-jordan-1-retro-high-og-shoes.png",
          },
        ],
        converted: true,
        conversionOrder: {
          orderId: "NIKE-2024-5498",
          orderTotal: 180,
          creditUsed: 103,
          newPayment: 77,
          products: [{ name: "Air Jordan 1 Retro High OG", price: 180 }],
          orderDate: new Date(Date.now() - 14300000).toISOString(),
        },
      },
      {
        id: "tb-003",
        sessionId: "ses-007",
        customerName: "Guest",
        itemCount: 1,
        totalCredit: 39,
        status: "declined",
        createdAt: new Date(Date.now() - 28800000).toISOString(),
        items: [
          {
            id: "item-6",
            name: "Tech Fleece Hoodie",
            condition: "fair",
            originalPrice: 130,
            creditValue: 39,
          },
        ],
        converted: false,
      },
    ]);

    // Nike PIN info
    if (id === "pin-nyc-001") {
      setPinInfo({
        name: "House of Innovation NYC",
        location: "New York, NY",
        status: "online",
      });
    } else if (id === "pin-soho-001") {
      setPinInfo({
        name: "Nike SoHo",
        location: "New York, NY",
        status: "online",
      });
    } else if (id === "pin-la-001") {
      setPinInfo({
        name: "Nike The Grove",
        location: "Los Angeles, CA",
        status: "online",
      });
    } else if (id === "pin-chi-001") {
      setPinInfo({
        name: "Nike Michigan Avenue",
        location: "Chicago, IL",
        status: "offline",
      });
    } else {
      setPinInfo({ name: "Nike PIN Device", location: id, status: "online" });
    }
  };

  const filteredMessages = selectedSession
    ? messages.filter((m) => m.sessionId === selectedSession)
    : messages;

  return (
    <div className="max-w-6xl">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link
          to="/pin"
          className="text-sm flex items-center gap-1 hover:underline"
          style={{ color: "#A9A9A9" }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M15 18L9 12L15 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to all PINs
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
              {pinInfo?.name || "Loading..."}
            </h1>
            {pinInfo && (
              <span
                className="px-2 py-0.5 rounded-full text-xs uppercase"
                style={{
                  backgroundColor:
                    pinInfo.status === "online"
                      ? "rgba(0,251,169,0.15)"
                      : "rgba(169,169,169,0.15)",
                  color: pinInfo.status === "online" ? "#00FBA9" : "#A9A9A9",
                }}
              >
                {pinInfo.status}
              </span>
            )}
          </div>
          <p className="text-sm" style={{ color: "#A9A9A9" }}>
            {pinInfo?.location} • Device ID: {id}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn-secondary text-sm">View Live</button>
          <button className="btn-secondary text-sm">Settings</button>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 border-b"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        <TabButton
          active={activeTab === "sessions"}
          onClick={() => setActiveTab("sessions")}
        >
          Sessions ({sessions.length})
        </TabButton>
        <TabButton
          active={activeTab === "chats"}
          onClick={() => setActiveTab("chats")}
        >
          Chats ({messages.length})
        </TabButton>
        <TabButton
          active={activeTab === "takebacks"}
          onClick={() => setActiveTab("takebacks")}
        >
          Takebacks ({takebacks.length})
        </TabButton>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <div
            className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
            style={{ borderColor: "#00FBA9" }}
          />
        </div>
      ) : (
        <>
          {/* Sessions Tab */}
          {activeTab === "sessions" && (
            <div
              className="bg-white border"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="divide-y"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedSession(session.id);
                      setActiveTab("chats");
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: "#FAFAFA" }}
                        >
                          <span style={{ color: "#171717" }}>
                            {session.customerName
                              ? session.customerName[0]
                              : "G"}
                          </span>
                        </div>
                        <div>
                          <p
                            className="font-medium"
                            style={{ color: "#171717" }}
                          >
                            {session.customerName || "Guest"}
                          </p>
                          <p className="text-sm" style={{ color: "#A9A9A9" }}>
                            {formatSessionType(session.sessionType)} •{" "}
                            {formatTime(session.startedAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p style={{ color: "#171717" }}>
                            {session.messageCount}
                          </p>
                          <p style={{ color: "#A9A9A9" }}>Messages</p>
                        </div>
                        <div className="text-center">
                          <p style={{ color: "#171717" }}>
                            {session.productsViewed}
                          </p>
                          <p style={{ color: "#A9A9A9" }}>Products</p>
                        </div>
                        <SessionBadge status={session.status} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chats Tab */}
          {activeTab === "chats" && (
            <div className="grid grid-cols-3 gap-6">
              {/* Session filter */}
              <div
                className="col-span-1 bg-white border p-4"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <p
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: "#A9A9A9" }}
                >
                  Filter by session
                </p>
                <button
                  onClick={() => setSelectedSession(null)}
                  className={`w-full text-left px-3 py-2 rounded text-sm mb-1 transition-colors ${!selectedSession ? "bg-gray-100" : "hover:bg-gray-50"}`}
                  style={{ color: "#171717" }}
                >
                  All chats
                </button>
                {sessions
                  .filter((s) => s.messageCount > 0)
                  .map((session) => (
                    <button
                      key={session.id}
                      onClick={() => setSelectedSession(session.id)}
                      className={`w-full text-left px-3 py-2 rounded text-sm mb-1 transition-colors ${selectedSession === session.id ? "bg-gray-100" : "hover:bg-gray-50"}`}
                      style={{ color: "#171717" }}
                    >
                      {session.customerName || "Guest"} ({session.messageCount})
                    </button>
                  ))}
              </div>

              {/* Chat messages */}
              <div
                className="col-span-2 bg-white border"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <div
                  className="px-6 py-4 border-b"
                  style={{ borderColor: "rgba(169,169,169,0.1)" }}
                >
                  <h3 className="font-medium" style={{ color: "#171717" }}>
                    {selectedSession
                      ? `Chat with ${sessions.find((s) => s.id === selectedSession)?.customerName || "Guest"}`
                      : "All Conversations"}
                  </h3>
                </div>
                <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                  {filteredMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className="max-w-md px-4 py-3"
                        style={{
                          backgroundColor:
                            message.role === "user" ? "#171717" : "#FAFAFA",
                          color:
                            message.role === "user" ? "#FFFFFF" : "#171717",
                        }}
                      >
                        <p className="text-sm">{message.content}</p>
                        {message.productContext && (
                          <div className="mt-2 p-2 rounded bg-white/10 flex items-center gap-2">
                            <div className="w-10 h-10 bg-gray-200 rounded" />
                            <div>
                              <p className="text-xs font-medium">
                                {message.productContext.name}
                              </p>
                              <p className="text-xs opacity-70">
                                ${message.productContext.price}
                              </p>
                            </div>
                          </div>
                        )}
                        <p className="text-xs mt-1 opacity-50">
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Takebacks Tab */}
          {activeTab === "takebacks" && (
            <div className="grid grid-cols-3 gap-6">
              {/* Takebacks List */}
              <div
                className={`${selectedTakeback ? "col-span-1" : "col-span-3"} bg-white border`}
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <div
                  className="divide-y"
                  style={{ borderColor: "rgba(169,169,169,0.1)" }}
                >
                  {takebacks.map((takeback) => (
                    <div
                      key={takeback.id}
                      className={`px-6 py-4 cursor-pointer transition-colors ${selectedTakeback?.id === takeback.id ? "bg-gray-50" : "hover:bg-gray-50"}`}
                      onClick={() => setSelectedTakeback(takeback)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "#FAFAFA" }}
                          >
                            <span style={{ color: "#171717" }}>
                              {takeback.customerName[0]}
                            </span>
                          </div>
                          <div>
                            <p
                              className="font-medium"
                              style={{ color: "#171717" }}
                            >
                              {takeback.customerName}
                            </p>
                            <p className="text-sm" style={{ color: "#A9A9A9" }}>
                              {takeback.itemCount} item
                              {takeback.itemCount !== 1 ? "s" : ""} •{" "}
                              {formatTime(takeback.createdAt)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p
                              className="font-medium"
                              style={{ color: "#00FBA9" }}
                            >
                              ${takeback.totalCredit}
                            </p>
                            <p className="text-xs" style={{ color: "#A9A9A9" }}>
                              Credit
                            </p>
                          </div>
                          {takeback.converted && (
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: "rgba(0,251,169,0.15)",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#00FBA9"
                                strokeWidth="2"
                              >
                                <path
                                  d="M20 6L9 17l-5-5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          )}
                          <TakebackBadge status={takeback.status} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Takeback Detail Panel */}
              {selectedTakeback && (
                <div className="col-span-2 space-y-6">
                  {/* Header */}
                  <div
                    className="bg-white border p-6"
                    style={{ borderColor: "rgba(169,169,169,0.2)" }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3
                          className="text-lg font-medium"
                          style={{ color: "#171717" }}
                        >
                          Trade-in #{selectedTakeback.id}
                        </h3>
                        <p className="text-sm" style={{ color: "#A9A9A9" }}>
                          {new Date(
                            selectedTakeback.createdAt,
                          ).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedTakeback(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            d="M18 6L6 18M6 6l12 12"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Customer Info */}
                    <div
                      className="flex items-center gap-4 p-4"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#171717" }}
                      >
                        <span className="text-white font-medium">
                          {selectedTakeback.customerName[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: "#171717" }}>
                          {selectedTakeback.customerName}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Items Traded */}
                  <div
                    className="bg-white border p-6"
                    style={{ borderColor: "rgba(169,169,169,0.2)" }}
                  >
                    <h4
                      className="font-medium mb-4"
                      style={{ color: "#171717" }}
                    >
                      Items Traded ({selectedTakeback.items.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedTakeback.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 p-3"
                          style={{ backgroundColor: "#FAFAFA" }}
                        >
                          <div className="w-16 h-16 overflow-hidden bg-gray-200">
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                No img
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <p
                              className="font-medium text-sm"
                              style={{ color: "#171717" }}
                            >
                              {item.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <ConditionBadge condition={item.condition} />
                              <span
                                className="text-xs"
                                style={{ color: "#A9A9A9" }}
                              >
                                Original: ${item.originalPrice}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className="font-medium"
                              style={{ color: "#00FBA9" }}
                            >
                              +${item.creditValue}
                            </p>
                            <p className="text-xs" style={{ color: "#A9A9A9" }}>
                              {Math.round(
                                (item.creditValue / item.originalPrice) * 100,
                              )}
                              % value
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="mt-4 pt-4 border-t flex justify-between items-center"
                      style={{ borderColor: "rgba(169,169,169,0.1)" }}
                    >
                      <span
                        className="font-medium"
                        style={{ color: "#171717" }}
                      >
                        Total Credit Issued
                      </span>
                      <span
                        className="text-xl font-medium"
                        style={{ color: "#00FBA9" }}
                      >
                        ${selectedTakeback.totalCredit}
                      </span>
                    </div>
                  </div>

                  {/* Conversion Status */}
                  <div
                    className="bg-white border p-6"
                    style={{ borderColor: "rgba(169,169,169,0.2)" }}
                  >
                    <h4
                      className="font-medium mb-4"
                      style={{ color: "#171717" }}
                    >
                      Conversion Status
                    </h4>
                    {selectedTakeback.converted &&
                    selectedTakeback.conversionOrder ? (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "rgba(0,251,169,0.15)" }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#00FBA9"
                              strokeWidth="2"
                            >
                              <path
                                d="M20 6L9 17l-5-5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span
                            className="font-medium"
                            style={{ color: "#00FBA9" }}
                          >
                            Converted to Sale
                          </span>
                        </div>

                        <div
                          className="p-4"
                          style={{ backgroundColor: "#FAFAFA" }}
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span
                              className="text-sm"
                              style={{ color: "#A9A9A9" }}
                            >
                              Order
                            </span>
                            <span
                              className="font-mono text-sm"
                              style={{ color: "#171717" }}
                            >
                              {selectedTakeback.conversionOrder.orderId}
                            </span>
                          </div>
                          <div className="space-y-2 mb-3">
                            {selectedTakeback.conversionOrder.products.map(
                              (p, i) => (
                                <div
                                  key={i}
                                  className="flex justify-between text-sm"
                                >
                                  <span style={{ color: "#171717" }}>
                                    {p.name}
                                  </span>
                                  <span style={{ color: "#A9A9A9" }}>
                                    ${p.price}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                          <div
                            className="border-t pt-3 space-y-2"
                            style={{ borderColor: "rgba(169,169,169,0.2)" }}
                          >
                            <div className="flex justify-between text-sm">
                              <span style={{ color: "#A9A9A9" }}>
                                Order Total
                              </span>
                              <span style={{ color: "#171717" }}>
                                ${selectedTakeback.conversionOrder.orderTotal}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: "#00FBA9" }}>
                                Credit Applied
                              </span>
                              <span style={{ color: "#00FBA9" }}>
                                -${selectedTakeback.conversionOrder.creditUsed}
                              </span>
                            </div>
                            <div className="flex justify-between font-medium">
                              <span style={{ color: "#171717" }}>
                                Customer Paid
                              </span>
                              <span style={{ color: "#171717" }}>
                                ${selectedTakeback.conversionOrder.newPayment}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-3 p-4"
                        style={{ backgroundColor: "#FAFAFA" }}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: "rgba(169,169,169,0.15)" }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#A9A9A9"
                            strokeWidth="2"
                          >
                            <path
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div>
                          <p
                            className="font-medium"
                            style={{ color: "#A9A9A9" }}
                          >
                            Not Converted
                          </p>
                          <p className="text-sm" style={{ color: "#A9A9A9" }}>
                            Credit not yet used for a purchase
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-3 text-sm font-medium transition-colors relative"
      style={{ color: active ? "#171717" : "#A9A9A9" }}
    >
      {children}
      {active && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5"
          style={{ backgroundColor: "#00FBA9" }}
        />
      )}
    </button>
  );
}

function SessionBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    active: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    completed: { bg: "rgba(169,169,169,0.15)", text: "#A9A9A9" },
    abandoned: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00" },
  };
  const c = colors[status] || colors.completed;

  return (
    <span
      className="px-2 py-1 rounded text-xs uppercase"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {status}
    </span>
  );
}

function TakebackBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    pending: { bg: "rgba(57,65,235,0.15)", text: "#3941EB" },
    completed: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    declined: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00" },
  };
  const c = colors[status] || colors.pending;

  return (
    <span
      className="px-2 py-1 rounded text-xs uppercase"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {status}
    </span>
  );
}

function formatSessionType(type: string): string {
  const labels: Record<string, string> = {
    browse: "Browsing",
    takeback: "Trade-in",
    voice_assist: "Voice Chat",
  };
  return labels[type] || type;
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function ConditionBadge({
  condition,
}: {
  condition: "excellent" | "good" | "fair";
}) {
  const colors: Record<string, { bg: string; text: string }> = {
    excellent: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    good: { bg: "rgba(57,65,235,0.15)", text: "#3941EB" },
    fair: { bg: "rgba(255,165,0,0.15)", text: "#FFA500" },
  };
  const c = colors[condition] || colors.good;

  return (
    <span
      className="px-2 py-0.5 rounded text-xs capitalize"
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {condition}
    </span>
  );
}
